import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, take, map, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

/**
 * Admin Guard
 *
 * Purpose:
 * - Restricts access to admin-only routes
 * - Reads role from AuthService.userRole$ (single source of truth)
 * - Waits for role to be determined before allowing/denying access
 * - Redirects non-admin users to the dashboard
 *
 * Usage:
 * Add to route configuration: canActivate: [AdminGuard]
 */
@Injectable({
  providedIn: 'root'
})
export class AdminGuard implements CanActivate {

  constructor(private router: Router, private authService: AuthService) {}

  /**
   * Check if user has admin or superuser role.
   * Returns Observable<boolean> so the guard waits for the role to be
   * emitted by AuthService before making the access decision.
   * 
   * Note: Uses authService.isAdmin which returns true for both 'admin' and 'superuser' roles
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.authService.userRole$.pipe(
      filter(role => role !== null), // wait until role is determined
      take(1),
      map(role => role === 'admin' || role === 'superuser'),
      tap(isAdmin => {
        if (!isAdmin) {
          this.router.navigate(['/dashboard']);
        } else {
        }
      })
    );
  }
}
